import {
  ChatRecordUpdateAction,
  ChatRecordUpdateCustomFieldChange,
  ChatRecordUpdateFieldChange,
} from "../../types/chat/ChatContent";
import { CustomFieldValueType } from "../../types/customField/CustomFieldValueType";

import { isRecord, RECORD_PARENT_ENTITY_TYPES } from "./chatWireGuards";

const parseChange = (value: unknown): ChatRecordUpdateFieldChange | undefined => {
  if (!isRecord(value) || typeof value.field !== "string" || !value.field) {
    return undefined;
  }
  // proposedValue must be present — an entry without one proposes nothing. currentValue may be
  // absent as well as null: BE writes `?? null` today, and both readings mean the record had no
  // value, so normalising here spares every renderer the distinction.
  if (!("proposedValue" in value)) {
    return undefined;
  }
  return {
    currentValue: value.currentValue ?? null,
    field: value.field,
    proposedValue: value.proposedValue,
  };
};

const parseCustomFieldChange = (value: unknown): ChatRecordUpdateCustomFieldChange | undefined => {
  if (
    !isRecord(value) ||
    typeof value.customFieldId !== "number" ||
    typeof value.esKey !== "string" ||
    !Array.isArray(value.value) ||
    !(value.currentValue === null || Array.isArray(value.currentValue))
  ) {
    return undefined;
  }
  // The guards above prove both are arrays, which every CustomFieldValueType variant the agent
  // sends is. The cast is the same one draftCustomFields makes: TypeScript cannot see that a single
  // field's items are homogeneous, so it will not narrow unknown[] to one of the variants.
  return {
    currentValue: value.currentValue as Exclude<CustomFieldValueType, undefined> | null,
    customFieldId: value.customFieldId,
    esKey: value.esKey,
    value: value.value as Exclude<CustomFieldValueType, undefined>,
  };
};

const parseRecordUpdateAction = (
  raw: Record<string, unknown>,
  warnings: string[]
): ChatRecordUpdateAction | undefined => {
  const entityType = RECORD_PARENT_ENTITY_TYPES.find((candidate) => candidate === raw.entityType);
  if (
    !entityType ||
    typeof raw.displayName !== "string" ||
    typeof raw.entityId !== "number" ||
    !Array.isArray(raw.changes)
  ) {
    return undefined;
  }

  // One unreadable row must not cost the rep the other three, so a failing entry is dropped with a
  // warning rather than sinking the whole action.
  const changes: ChatRecordUpdateFieldChange[] = [];
  raw.changes.forEach((entry) => {
    const change = parseChange(entry);
    if (change) {
      changes.push(change);
    } else {
      warnings.push('Malformed "record_update" change entry');
    }
  });

  const customFields: ChatRecordUpdateCustomFieldChange[] = [];
  if (Array.isArray(raw.customFields)) {
    raw.customFields.forEach((entry) => {
      const customField = parseCustomFieldChange(entry);
      if (customField) {
        customFields.push(customField);
      } else {
        warnings.push('Malformed "record_update" customFields entry');
      }
    });
  }

  // An empty changes array is legal only when custom fields carry the payload. Both empty leaves
  // nothing to render and nothing to confirm, so the proposal degrades to the unknown sentinel.
  if (!changes.length && !customFields.length) {
    return undefined;
  }

  return {
    changes,
    // Omitted rather than sent empty: [] means "wipe every proposed custom-field edit" downstream.
    ...(customFields.length ? { customFields } : {}),
    displayName: raw.displayName,
    entityId: raw.entityId,
    entityType,
    type: "record_update",
  };
};

export default parseRecordUpdateAction;
